/**
 * Browser Notification Helper
 * Handles native browser notifications for chat messages
 */

class BrowserNotificationHelper {
  constructor() {
    this.permissionGranted = false;
    this.checkPermission();
  }

  checkPermission() {
    if (!('Notification' in window)) {
      // eslint-disable-next-line no-console
      console.warn('Browser does not support notifications');
      return false;
    }
    this.permissionGranted = Notification.permission === 'granted';
    return this.permissionGranted;
  }

  async requestPermission() {
    if (!('Notification' in window)) {
      return false;
    }

    if (Notification.permission === 'granted') {
      this.permissionGranted = true;
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      this.permissionGranted = permission === 'granted';
      return this.permissionGranted;
    }

    return false;
  }

  /**
   * Show a browser notification
   * @param {Object} options - Notification options
   * @param {string} options.title - Notification title
   * @param {string} options.body - Notification body/message
   * @param {string} options.icon - Icon URL (optional)
   * @param {string} options.tag - Tag to group notifications (optional)
   * @param {Function} options.onClick - Callback when notification is clicked (optional)
   */
  show({ title, body, icon, tag, onClick }) {
    if (!this.permissionGranted) {
      this.requestPermission().then(granted => {
        if (granted) {
          this.createNotification({ title, body, icon, tag, onClick });
        }
      });
      return;
    }

    this.createNotification({ title, body, icon, tag, onClick });
  }

  // eslint-disable-next-line class-methods-use-this
  createNotification({ title, body, icon, tag, onClick }) {
    // Don't show notification if window is focused
    if (document.hasFocus()) {
      return;
    }

    const notification = new Notification(title, {
      body,
      icon: icon || '/favicon.ico',
      tag: tag || 'chatsuite-notification',
      requireInteraction: false,
      silent: false, // Let audio helper handle sound
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
      if (onClick) {
        onClick();
      }
    };

    // Auto close after 5 seconds
    setTimeout(() => {
      notification.close();
    }, 5000);
  }

  /**
   * Show notification for chat room message
   * @param {Object} message - The message object
   * @param {Object} room - The chat room object
   */
  showChatRoomNotification(message, room) {
    const senderName =
      message.sender?.name || message.sender?.email || 'Alguém';
    const roomName = room?.name || 'Chat';
    const messageContent = message.content || 'Nova mensagem';

    // Truncate message if too long
    const truncatedContent =
      messageContent.length > 100
        ? `${messageContent.substring(0, 100)}...`
        : messageContent;

    this.show({
      title: `${senderName} em ${roomName}`,
      body: truncatedContent,
      tag: `chatroom-${room?.id || 'default'}`,
      onClick: () => {
        // Focus will be handled by notification.onclick
      },
    });
  }
}

// Export singleton instance
const browserNotificationHelper = new BrowserNotificationHelper();

// Also expose globally for easy access
window.showBrowserNotification = options =>
  browserNotificationHelper.show(options);
window.showChatRoomNotification = (message, room) =>
  browserNotificationHelper.showChatRoomNotification(message, room);

export default browserNotificationHelper;
